import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

import type { TeamRecord } from '@/domain/teamTypes';
import { useAuth } from '@/hooks/useAuth';
import { pb } from '@/lib/pocketbase';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PendingInvitations } from '@/components/teams/PendingInvitations';
import { TeamDetail } from '@/components/teams/TeamDetail';

export function TeamsPage() {
  const { user } = useAuth();
  const [teams, setTeams] = useState<TeamRecord[] | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const records = await pb.collection('teams').getFullList<TeamRecord>({ sort: 'name' });
        setTeams(records);
      } catch {
        toast.error('Impossible de charger vos équipes.');
        setTeams([]);
      }
    })();
  }, [refreshToken]);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!user || !newTeamName.trim()) return;
    setIsCreating(true);
    try {
      await pb.collection('teams').create({ name: newTeamName.trim(), owner: user.id });
      setNewTeamName('');
      setRefreshToken((t) => t + 1);
      toast.success('Équipe créée.');
    } catch {
      toast.error("Impossible de créer l'équipe.");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <AppShell>
      <main className="flex flex-col gap-4 p-4 sm:p-6">
        <PendingInvitations />

        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-2xl font-bold tracking-tight">Mes équipes</h2>
          <form onSubmit={handleCreate} className="flex gap-2">
            <Input
              placeholder="Nom de l'équipe"
              className="min-w-[180px]"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
            />
            <Button type="submit" disabled={isCreating}>
              <Plus />
              Créer
            </Button>
          </form>
        </div>

        {teams === null && <p className="text-sm text-muted-foreground">Chargement…</p>}
        {teams !== null && teams.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Aucune équipe pour l’instant — crée-en une pour collaborer.
          </p>
        )}

        <div className="flex flex-col gap-4">
          {teams?.map((team) => (
            <Card key={team.id} className="overflow-hidden py-0">
              <CardHeader
                className="cursor-pointer border-b bg-card py-4"
                onClick={() => setExpandedId((id) => (id === team.id ? null : team.id))}
              >
                <CardTitle>{team.name}</CardTitle>
              </CardHeader>
              {expandedId === team.id && (
                <CardContent className="py-4">
                  <TeamDetail team={team} />
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      </main>
    </AppShell>
  );
}
